import { useCallback, useEffect, useState } from 'react';
import { Env } from '../../shared';
import { useAdminApi, ApiError } from './client';

/**
 * Venue reward catalog: read one venue's rewards + enrollment, create, edit,
 * retire, and toggle enrollment.
 *
 * `coins_cost_out_of_bounds` is a soft 400 rather than a hard rejection — the
 * caller re-sends with `allowOutOfBounds` once an admin has confirmed the
 * unusual value. The mutations surface that as `OutOfBoundsError` so the panel
 * can prompt rather than just showing a failure.
 */

export interface VenueReward {
  id: string;
  venue_id: string;
  template_key: string | null;
  coins_cost: number;
  description: string;
  is_active: boolean;
  moderation_status: 'approved' | 'pending' | 'rejected';
  created_at: string;
  updated_at: string;
  redemption_count: number;
  /** Optional recurring redemption window. All three are null together (no
   *  window — always redeemable) or all set. Days use EXTRACT(dow) numbering,
   *  0 = Sunday, matching the V143 CHECK constraint and the business-app API. */
  redeem_days: number[] | null;
  redeem_start_local: string | null;  // "HH:MM"
  redeem_end_local: string | null;    // "HH:MM"
}

export interface RewardVenue {
  id: string;
  name: string;
  is_enrolled: boolean;
  enrollment_active: boolean;
  enrolled_at: string | null;
}

interface VenueRewardsResponse {
  venue: RewardVenue;
  items: VenueReward[];
  count: number;
}

export const TEMPLATE_KEYS = [
  'line_skip',
  '5_off_tab',
  'free_first_drink',
  'free_cover',
  'two_for_one',
  'free_water',
] as const;

export type TemplateKey = (typeof TEMPLATE_KEYS)[number];

/** Labels for the pre-vetted templates — the key is a slug, not display copy. */
export const TEMPLATE_LABELS: Record<TemplateKey, string> = {
  line_skip: 'Skip the line',
  '5_off_tab': '$5 off your tab',
  free_first_drink: 'Free first drink',
  free_cover: 'Free cover',
  two_for_one: 'Two for one',
  free_water: 'Free water',
};

export const COINS_MIN = 50;
export const COINS_MAX = 1000;

/** Thrown when the backend rejects a cost outside the usual range. */
export class OutOfBoundsError extends Error {
  constructor(public readonly min: number, public readonly max: number) {
    super(`Outside the usual ${min}–${max} range.`);
    this.name = 'OutOfBoundsError';
  }
}

/** Window fields as the API takes them. Send all three, or none. Sending all
 *  three as null on an update clears the window. */
export interface RewardWindowInput {
  redeem_days?: number[] | null;
  redeem_start_local?: string | null;
  redeem_end_local?: string | null;
}

export interface CreateRewardInput extends RewardWindowInput {
  templateKey?: TemplateKey | null;
  description?: string;
  coinsCost: number;
  allowOutOfBounds?: boolean;
}

export interface UpdateRewardInput extends RewardWindowInput {
  coinsCost?: number;
  description?: string;
  isActive?: boolean;
  allowOutOfBounds?: boolean;
}

export function useVenueRewards(env: Env, venueId: string | null) {
  const { request } = useAdminApi();
  const [venue, setVenue] = useState<RewardVenue | null>(null);
  const [items, setItems] = useState<VenueReward[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!venueId) {
      setVenue(null);
      setItems([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await request<VenueRewardsResponse>({
        env,
        path: `/admin/gamification/venues/${venueId}/rewards`,
      });
      setVenue(data.venue);
      setItems(data.items);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load rewards');
      setVenue(null);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [request, env, venueId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const rethrowBounds = (e: unknown): never => {
    if (e instanceof ApiError && e.code === 'coins_cost_out_of_bounds') {
      throw new OutOfBoundsError(COINS_MIN, COINS_MAX);
    }
    throw e;
  };

  const create = useCallback(
    async (input: CreateRewardInput): Promise<boolean> => {
      if (!venueId) return false;
      setActionError(null);
      try {
        await request({
          env,
          method: 'POST',
          path: `/admin/gamification/venues/${venueId}/rewards`,
          body: input,
        });
        await refetch();
        return true;
      } catch (e) {
        if (e instanceof ApiError && e.code === 'coins_cost_out_of_bounds') {
          rethrowBounds(e);
        }
        setActionError(e instanceof ApiError ? e.message : 'Failed to create reward');
        return false;
      }
    },
    [request, env, venueId, refetch],
  );

  const update = useCallback(
    async (rewardId: string, input: UpdateRewardInput): Promise<boolean> => {
      setActionError(null);
      try {
        await request({
          env,
          method: 'PUT',
          path: `/admin/gamification/rewards/${rewardId}`,
          body: input,
        });
        await refetch();
        return true;
      } catch (e) {
        if (e instanceof ApiError && e.code === 'coins_cost_out_of_bounds') {
          rethrowBounds(e);
        }
        setActionError(e instanceof ApiError ? e.message : 'Failed to update reward');
        return false;
      }
    },
    [request, env, refetch],
  );

  const remove = useCallback(
    async (rewardId: string): Promise<boolean> => {
      setActionError(null);
      try {
        await request({
          env,
          method: 'DELETE',
          path: `/admin/gamification/rewards/${rewardId}`,
        });
        await refetch();
        return true;
      } catch (e) {
        setActionError(e instanceof ApiError ? e.message : 'Failed to delete reward');
        return false;
      }
    },
    [request, env, refetch],
  );

  const setEnrollment = useCallback(
    async (isActive: boolean): Promise<boolean> => {
      if (!venueId) return false;
      setActionError(null);
      try {
        await request({
          env,
          method: 'PUT',
          path: `/admin/gamification/venues/${venueId}/enrollment`,
          body: { isActive },
        });
        await refetch();
        return true;
      } catch (e) {
        setActionError(e instanceof ApiError ? e.message : 'Failed to update enrollment');
        return false;
      }
    },
    [request, env, venueId, refetch],
  );

  return {
    venue,
    items,
    loading,
    error,
    actionError,
    refetch,
    create,
    update,
    remove,
    setEnrollment,
  };
}
